import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { vehicleFormSchema } from '@greasemap/validation';
import { useVehicle } from '../../garage/queries';
import { useCreateVehicle, useUpdateVehicle } from '../../garage/mutations';
import type { VinDecode } from '../../garage/types';
import { Button, Card, ErrorState, Heading, Spinner, Stack, Text, TextField } from '../../ui';
import { VinLookupStep } from './VinLookupStep';

interface FormState {
  year: string;
  make: string;
  model: string;
  trim: string;
  vin: string;
  nickname: string;
  isPublic: boolean;
}

const EMPTY: FormState = {
  year: '',
  make: '',
  model: '',
  trim: '',
  vin: '',
  nickname: '',
  isPublic: false,
};

/**
 * Creates or edits a vehicle.
 *
 * Creating starts at the VIN step; editing goes straight to the fields, because the vehicle already
 * exists and re-decoding it would be busywork.
 *
 * @returns The vehicle form screen.
 */
export function VehicleFormPage(): React.JSX.Element {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const isEdit = vehicleId !== undefined;
  const navigate = useNavigate();

  const existing = useVehicle(isEdit ? vehicleId : '');
  const create = useCreateVehicle();
  const update = useUpdateVehicle(vehicleId ?? '');

  const [showFields, setShowFields] = useState(isEdit);
  const [values, setValues] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Seed the form from the server exactly once per vehicle, so typing is never overwritten by a
  // background refetch.
  if (isEdit && existing.data !== undefined && loadedId !== existing.data.id) {
    const loaded = existing.data;
    setLoadedId(loaded.id);
    setValues({
      year: String(loaded.year),
      make: loaded.make,
      model: loaded.model,
      trim: loaded.trim ?? '',
      vin: loaded.vin ?? '',
      nickname: loaded.nickname ?? '',
      isPublic: loaded.isPublic,
    });
  }

  function applyDecode(decoded: VinDecode): void {
    setValues((current) => ({
      ...current,
      year:
        decoded.year === null || decoded.year === undefined ? current.year : String(decoded.year),
      make: decoded.make ?? current.make,
      model: decoded.model ?? current.model,
      trim: decoded.trim ?? current.trim,
      vin: decoded.vin,
    }));
    setShowFields(true);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(null);

    const parsed = vehicleFormSchema.safeParse({
      year: values.year === '' ? Number.NaN : Number(values.year),
      make: values.make,
      model: values.model,
      trim: values.trim,
      vin: values.vin,
      nickname: values.nickname,
      isPublic: values.isPublic,
    });

    if (!parsed.success) {
      const next: Record<string, string> = {};

      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        next[key] ??= issue.message;
      }

      setErrors(next);
      return;
    }

    setErrors({});

    const body = {
      year: parsed.data.year,
      make: parsed.data.make,
      model: parsed.data.model,
      trim: parsed.data.trim ?? null,
      vin: parsed.data.vin === undefined || parsed.data.vin === '' ? null : parsed.data.vin,
      nickname: parsed.data.nickname ?? null,
      isPublic: parsed.data.isPublic,
      version: isEdit ? (existing.data?.version ?? null) : null,
    };

    try {
      const saved = isEdit ? await update.mutateAsync(body) : await create.mutateAsync(body);
      await navigate(`/garage/${saved.id}`);
    } catch (cause) {
      setSubmitError(cause instanceof Error ? cause.message : 'Could not save that vehicle.');
    }
  }

  if (isEdit && existing.isPending) {
    return (
      <Stack gap="md" align="center">
        <Spinner label="Loading the vehicle" />
        <Text muted>Loading…</Text>
      </Stack>
    );
  }

  if (isEdit && existing.isError) {
    return <ErrorState message={existing.error.message} onRetry={() => void existing.refetch()} />;
  }

  return (
    <Stack gap="xl">
      <Heading level={1}>{isEdit ? 'Edit vehicle' : 'Add a vehicle'}</Heading>

      {!showFields && (
        <VinLookupStep
          onAccept={applyDecode}
          onSkip={(vin) => {
            if (vin !== undefined) {
              setValues((current) => ({ ...current, vin }));
            }

            setShowFields(true);
          }}
        />
      )}

      {showFields && (
        <Card ariaLabel="Vehicle details">
          <form onSubmit={(event) => void submit(event)}>
            <Stack gap="lg">
              <TextField
                label="Year"
                inputMode="numeric"
                value={values.year}
                onChange={(event) => setValues({ ...values, year: event.target.value })}
                error={errors['year']}
              />
              <TextField
                label="Make"
                value={values.make}
                onChange={(event) => setValues({ ...values, make: event.target.value })}
                error={errors['make']}
              />
              <TextField
                label="Model"
                value={values.model}
                onChange={(event) => setValues({ ...values, model: event.target.value })}
                error={errors['model']}
              />
              <TextField
                label="Trim"
                value={values.trim}
                onChange={(event) => setValues({ ...values, trim: event.target.value })}
                error={errors['trim']}
              />
              <TextField
                label="VIN"
                value={values.vin}
                maxLength={17}
                onChange={(event) => setValues({ ...values, vin: event.target.value })}
                error={errors['vin']}
                hint="Optional. Leave blank if you do not have it."
              />
              <TextField
                label="Nickname"
                value={values.nickname}
                onChange={(event) => setValues({ ...values, nickname: event.target.value })}
                error={errors['nickname']}
              />

              <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={values.isPublic}
                  onChange={(event) => setValues({ ...values, isPublic: event.target.checked })}
                />
                <span>Visible to other users</span>
              </label>

              <Text size="sm" muted>
                Your VIN is never shown to anyone else, even when the vehicle is public.
              </Text>

              {submitError !== null && (
                <p className="gm-field-error" role="alert">
                  {submitError}
                </p>
              )}

              <Stack direction="row" gap="sm" wrap>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={create.isPending || update.isPending}
                >
                  {isEdit ? 'Save changes' : 'Add vehicle'}
                </Button>
                <Button onClick={() => void navigate(isEdit ? `/garage/${vehicleId}` : '/garage')}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </form>
        </Card>
      )}
    </Stack>
  );
}
